// "Mis reportes" tab — saved filter-builder reports. Lives next to the
// pre-built reports inside ReportsClientsView. Each row in the list is a
// CustomReport the user has saved; the builder modal lets them create or
// edit one with arbitrary AND/OR groups.
//
// Phase 2A scope:
//   - List / create / edit / delete reports (owner only for edit/delete).
//   - Nested AND/OR groups via a recursive filter tree.
//   - Column customization (pick which columns the result table shows).
//   - Share with the rest of the agency (read-only for non-owners).
//
// The results table + Excel/CSV export is rendered by the parent component
// (ReportsClientsView) so saved reports and pre-built reports share one UI.

import { useEffect, useMemo, useState } from 'react';
import { COLUMNAS_DE_DEPENDIENTES, etiquetaDeDependiente, etiquetaDeEstatusDeDependiente } from '../columnasDeDependientes.js';
import DateField from './DateField.jsx';
import {
  Plus, Edit2, Trash2, Play, Save, X, Sparkles, Users, Folder, FolderTree,
} from 'lucide-react';
import {
  listCustomReports, createCustomReport, updateCustomReport,
  deleteCustomReport, runCustomReport, listUsers, getAgencySettings,
  getClientFilterOptions,
} from '../api.js';
import { PREFIJO_LIBRE, opcionesDeAtiendeParaReporte } from '../atiende.js';
import { toast } from './Toast.jsx';
import { POLICY_TYPES, ENUM_A_TIPO, esValorDeEnum } from '../policyTypes.js';
import { ordenarCamposDeReporte } from '../utils.js';
import { confirmDialog } from './ui.jsx';
import { hasSeatPermission } from '../seatPermissions.js';
import { FiltroMultiple } from './FiltroMultiple.jsx';
import { estatusParaElegir, etiquetasDeEstatus } from '../estatusMigratorio.js';

// ─ Field catalog ──────────────────────────────────────────────────────
// Each entry describes one filterable field: the backend key the API
// expects, the human label, the input type, and the operators it allows.
// Adding a new field = drop one entry here AND add a case in the backend
// `clientPredicate()`. Keys must match `CustomReportField` in
// `deskte-api/src/utils/customReportFilter.ts`.

// Tiene que llevar TODOS los valores del enum PolicyStatus de Prisma. Faltaba
// GRACE_PERIOD, asi que las polizas en periodo de gracia no se podian sacar en
// ningun reporte — y son justo las que hay que llamar antes de que se caigan
// (Lissette 2026-08-25).
const POLICY_STATUS_OPTIONS = ['ACTIVE', 'PENDING', 'GRACE_PERIOD', 'EXPIRED', 'CANCELLED', 'LAPSED'];
const CLIENT_STATUS_OPTIONS = ['ACTIVE', 'INACTIVE', 'PROSPECT', 'REFERRAL'];

// Los dos desplegables de estatus enseñaban el valor crudo de la base de datos
// (ACTIVE, GRACE_PERIOD...). El valor que se GUARDA sigue siendo ese — es lo que
// el backend compara contra el enum de Prisma — pero lo que se lee ya no.
const CLIENT_STATUS_LABELS = (es) => es
  ? { ACTIVE: 'Activo', INACTIVE: 'Inactivo', PROSPECT: 'Prospecto', REFERRAL: 'Referido' }
  : { ACTIVE: 'Active', INACTIVE: 'Inactive', PROSPECT: 'Prospect', REFERRAL: 'Referral' };
const POLICY_STATUS_LABELS = (es) => es
  ? { ACTIVE: 'Activa', PENDING: 'Pendiente', GRACE_PERIOD: 'Período de gracia', EXPIRED: 'Vencida', CANCELLED: 'Cancelada', LAPSED: 'Caída' }
  : { ACTIVE: 'Active', PENDING: 'Pending', GRACE_PERIOD: 'Grace period', EXPIRED: 'Expired', CANCELLED: 'Cancelled', LAPSED: 'Lapsed' };
// Enum values mirror what the ClientForm dropdown sends so saved filters
// match real rows. Translation to display labels lives in the option list
// of the value input below.
const MARITAL_STATUS_OPTIONS = ['single', 'married', 'widowed', 'divorced', 'separated', 'domestic_partner'];
// Must mirror ImmigrationSelect in deskte-full.jsx (the legacy form) so the
// filter dropdown matches the values actually stored on real clients.
// La lista y las etiquetas vienen de crm-app/estatusMigratorio.js.
//
// Aqui habia una copia a mano. El 2026-09-01 hubo que anadirle 'undocumented'
// porque faltaba, y esa misma correccion NO llego al mapa de la descarga --
// de ahi la queja 11. Copiar la lista es como se separan: ahora hay una sola.
// Se pide al DIBUJAR, no al importar: los estatus propios de la agencia se
// hidratan cuando llegan los ajustes, despues de que se cargue este archivo.
// Congelarla aqui dejaria el filtro sin los que alguien acaba de escribir.
const IMMIGRATION_STATUS_OPTIONS = () => estatusParaElegir();
const IMMIGRATION_LABELS = (es) => etiquetasDeEstatus(es);

// VARIOS VALORES EN UNA SOLA CONDICIÓN (Lissette, 2026-09-01):
//
//   "quiere que el titular sea ciudadano y poder escoger otro estatus como
//    residente... pero solo puede escoger una condición, si no todos sale sin
//    nada".
//
// Con solo "es igual a", pedir ciudadano Y residente es lo que se manda de
// verdad al servidor — y nadie tiene dos estatus a la vez, así que la lista
// sale vacía. Se podía resolver anidando un grupo "O", pero eso no lo
// descubre quien no programa: lo que ve es un reporte que no sirve.
const LIST_OPS = ['is_any_of', 'is_none_of'];
const esOpDeLista = (op) => LIST_OPS.includes(op);

/**
 * @param lang
 * @param etiquetas  { cliente: string[], poliza: string[] } — los catálogos de
 *   la agencia. Cuando llegan vacíos (aún cargando, o una agencia que nunca
 *   creó ninguna), el selector cae solo a escribir la etiqueta a mano: es lo
 *   que hace `ConditionValueInput` con una lista de opciones vacía, así que no
 *   hay que hacer nada especial aquí.
 * @param atiende  { asientos: [{id,nombre,n}], nombres: [{nombre,n}] } — quién
 *   lleva clientes HOY en esta agencia, del servidor.
 */
function makeFields(lang, etiquetas = { cliente: [], poliza: [] }, atiende = null) {
  const es = lang === 'es';
  // "es cualquiera de" va en TODOS los campos de texto (Marilyn Sequera,
  // 2026-09-09: *"que se puedan seleccionar varios estados"*; Lissette: *"a
  // casi todo lo que podamos escoger mas de uno"*). Va DESPUES de "es igual
  // a" a proposito: el primero de la lista es el que sale al elegir el campo,
  // y en un nombre o una direccion lo normal sigue siendo buscar uno solo.
  const TEXT_OPS = ['eq', 'neq', 'is_any_of', 'is_none_of', 'contains', 'starts_with', 'ends_with', 'is_empty', 'is_not_empty'];
  // Los campos que viven dentro de type_details: los mismos, sin
  // starts_with/ends_with (se comparan por su valor entero, no por trozos).
  const DETAIL_OPS = ['eq', 'neq', 'is_any_of', 'is_none_of', 'contains', 'is_empty', 'is_not_empty'];
  const NUM_OPS = ['eq', 'neq', 'gt', 'gte', 'lt', 'lte', 'between'];
  const DATE_OPS = ['eq', 'before', 'after', 'between', 'is_empty', 'is_not_empty'];
  const BOOL_OPS = ['eq', 'neq'];
  const LANG_LABELS = es ? { en: 'Inglés', es: 'Español' } : { en: 'English', es: 'Spanish' };
  return [
    // ─ Identity / contact ─────────────────────────────────────────────
    { key: 'firstName', label: es ? 'Nombre' : 'First name', input: 'text', ops: TEXT_OPS },
    { key: 'lastName',  label: es ? 'Apellido' : 'Last name', input: 'text', ops: TEXT_OPS },
    // email: encrypted at rest — only EXACT match works (via the deterministic
    // fingerprint), so no contains/starts_with. The primary phone is encrypted
    // with no fingerprint at all, so it isn't filterable here — filter on
    // 'phoneSecondary' (a plaintext column) instead.
    { key: 'email',     label: 'Email', input: 'text', ops: ['eq', 'neq', 'is_empty', 'is_not_empty'] },
    { key: 'city',      label: es ? 'Ciudad' : 'City', input: 'text', ops: TEXT_OPS },
    { key: 'state',     label: es ? 'Estado' : 'State', input: 'text', ops: TEXT_OPS },
    { key: 'status',    label: es ? 'Estado del cliente' : 'Client status', input: 'enumLabeled',
      options: CLIENT_STATUS_OPTIONS, labels: CLIENT_STATUS_LABELS(es),
      ops: ['is_any_of', 'is_none_of', 'eq', 'neq'] },
    { key: 'source',    label: es ? 'Origen' : 'Source', input: 'text', ops: TEXT_OPS },
    // ETIQUETAS DEL CLIENTE (Lissette, 2026-09-13: *"y también poder tener las
    // etiquetas personalizadas para poder hacer reportes también"*).
    //
    // Se podía filtrar por etiqueta desde siempre, pero había que ESCRIBIRLA a
    // mano y solo una: sin la lista delante, un reporte por etiqueta era
    // adivinar cómo se escribió. Ahora se marcan de la lista de la agencia y se
    // pueden marcar varias — 'es cualquiera de' va primero porque es lo que se
    // quiere el 99% de las veces.
    //
    // Y el detalle que hacía daño en silencio: la comparación era EXACTA, así
    // que escribir "vip" no encontraba a quien tenía "VIP" y el reporte salía
    // vacío. El servidor busca ahora las cuatro escrituras.
    { key: 'tags', label: es ? 'Etiquetas del cliente' : 'Client tags', input: 'enumLabeled',
      options: etiquetas.cliente || [],
      ops: ['is_any_of', 'is_none_of', 'contains', 'is_empty', 'is_not_empty'] },
    { key: 'county',    label: es ? 'Condado' : 'County', input: 'text', ops: TEXT_OPS },
    { key: 'phoneSecondary', label: es ? 'Teléfono secundario' : 'Secondary phone', input: 'text', ops: TEXT_OPS },
    { key: 'preferredLanguage', label: es ? 'Idioma' : 'Language', input: 'enumLabeled',
      options: ['en', 'es'], labels: LANG_LABELS, ops: ['eq', 'neq', 'is_any_of', 'is_none_of'] },
    { key: 'assignedAgent', label: es ? 'Agente' : 'Agent', input: 'text', ops: TEXT_OPS },
    // ATIENDE / HANDLE (Lissette, 2026-09-15: *"en el reporte de cliente tener
    // la opción de hacer un reporte por handle y colocar cualquiera de y los
    // nombres de los handles o vacío"*).
    //
    // Va JUSTO DEBAJO del agente asignado porque es la pregunta de al lado y la
    // que más se confunde con ella: el agente asignado es de quién es la
    // póliza; "Atiende" es quién lleva al cliente. Son dos casillas distintas
    // de la misma ficha y la agencia usa las dos.
    //
    // 'is_any_of' PRIMERO —es el que se pidió— y 'está vacío' es la otra mitad
    // de la respuesta: los clientes que no atiende nadie, que es lo que se
    // busca para repartirlos.
    { key: 'attendedBy', label: es ? 'Atiende (handle)' : 'Handled by', input: 'enumLabeled',
      ...opcionesDeAtiendeParaReporte(atiende, es),
      // Cómo se lee un valor que NO está en el catálogo de hoy: mientras la
      // lista llega del servidor, y para un handle que ya no lleva a nadie. Un
      // nombre suelto se enseña sin su prefijo; un asiento borrado enseña su id
      // crudo, que es feo pero no miente.
      etiquetaDe: (v) => (String(v || '').startsWith(PREFIJO_LIBRE)
        ? String(v).slice(PREFIJO_LIBRE.length)
        : String(v || '')),
      ops: ['is_any_of', 'is_none_of', 'eq', 'neq', 'is_empty', 'is_not_empty'] },
    // ─ Household + life context ───────────────────────────────────────
    { key: 'maritalStatus', label: es ? 'Estado civil' : 'Marital status', input: 'enumLabeled',
      options: MARITAL_STATUS_OPTIONS,
      labels: es
        ? { single: 'Soltero/a', married: 'Casado/a', widowed: 'Viudo/a', divorced: 'Divorciado/a', separated: 'Separado/a', domestic_partner: 'Unión de hecho' }
        : { single: 'Single', married: 'Married', widowed: 'Widowed', divorced: 'Divorced', separated: 'Separated', domestic_partner: 'Domestic partner' },
      ops: ['is_any_of', 'is_none_of', 'eq', 'neq', 'is_empty', 'is_not_empty'] },
    // ¿FUMA? (Wendy Cercio, 2026-10-30: "como filtro en un reporte si el cliente
    // fuma"; "chequear para todos los residentes y ciudadanos si está vacío ese
    // campo o lleno"). Vacío = "Sin preguntar" en la ficha (E-403).
    { key: 'smoker', label: es ? '¿Fuma?' : 'Smoker?', input: 'enumLabeled',
      options: ['yes', 'no'], labels: es ? { yes: 'Sí fuma', no: 'No fuma' } : { yes: 'Smoker', no: 'Non-smoker' },
      ops: ['is_any_of', 'is_none_of', 'eq', 'neq', 'is_empty', 'is_not_empty'] },
    { key: 'spouseSmoker', label: es ? 'Cónyuge — ¿Fuma?' : 'Spouse — Smoker?', input: 'enumLabeled',
      options: ['yes', 'no'], labels: es ? { yes: 'Sí fuma', no: 'No fuma' } : { yes: 'Smoker', no: 'Non-smoker' },
      ops: ['is_any_of', 'is_none_of', 'eq', 'neq', 'is_empty', 'is_not_empty'] },
    { key: 'immigrationStatus', label: es ? 'Estatus migratorio' : 'Immigration status', input: 'enumLabeled',
      options: IMMIGRATION_STATUS_OPTIONS(), labels: IMMIGRATION_LABELS(es),
      // 'is_any_of' va PRIMERO: es el que quiere el 99% de los reportes, y el
      // primero de la lista es el que sale por defecto al elegir el campo.
      ops: ['is_any_of', 'is_none_of', 'eq', 'neq', 'is_empty', 'is_not_empty'] },
    // VENCIMIENTO DEL DOCUMENTO MIGRATORIO (Lissette 2026-09-13: "me gustaría
    // poder tomar el vencimiento de la tarjeta de permiso de trabajo o de green
    // card para poder hacer reporte de eso cuando se vence").
    //
    // Va JUSTO DEBAJO del estatus migratorio porque los dos se usan juntos: el
    // estatus dice QUÉ documento es (permiso de trabajo, residente…) y éste
    // CUÁNDO se le acaba. Cada uno solo no sirve para llamar a nadie.
    //
    // 'before' va PRIMERO porque es la pregunta real ("¿a quién se le vence
    // antes de tal fecha?") y es lo primero que se ve al abrir la lista. OJO:
    // NO es el valor que sale marcado — `onFieldChange` conserva el operador
    // anterior si el campo nuevo lo admite, así que viniendo de un "es igual a"
    // se queda en "es igual a". Es el comportamiento de todos los campos de
    // fecha del constructor y no se cambia aquí por uno solo; comprobado en el
    // navegador el 2026-09-14.
    //
    // La fecha vive dentro del bloque cifrado de documentos, así que el
    // servidor la resuelve descifrando después de la consulta. Consecuencia
    // visible: NO se puede usar dentro de un grupo "O" — el servidor lo
    // rechaza con un aviso, y la pantalla lo dice antes (ver SOLO_CON_Y).
    { key: 'immigrationDocExpiration',
      label: es ? 'Vencimiento del documento (permiso / green card)' : 'Document expiration (work permit / green card)',
      input: 'date', ops: ['before', 'between', 'after', 'eq', 'is_empty', 'is_not_empty'] },
    { key: 'employer',       label: es ? 'Empleador' : 'Employer', input: 'text', ops: TEXT_OPS },
    { key: 'annualIncome',   label: es ? 'Ingresos anuales' : 'Annual income', input: 'text', ops: TEXT_OPS },
    { key: 'householdSize',  label: es ? 'Tamaño del hogar' : 'Household size', input: 'number', ops: NUM_OPS },
    { key: 'insuredMembers', label: es ? 'Miembros asegurados' : 'Insured members', input: 'number', ops: NUM_OPS },
    { key: 'placeOfBirth',      label: es ? 'Lugar de nacimiento' : 'Place of birth', input: 'text', ops: TEXT_OPS },
    { key: 'driverLicenseState', label: es ? 'Estado de licencia' : 'Driver license state', input: 'text', ops: TEXT_OPS },
    { key: 'hasDui',    label: es ? '¿Tiene DUI?' : 'Has DUI', input: 'bool', ops: BOOL_OPS },
    { key: 'hasFelony', label: es ? '¿Tiene felonía?' : 'Has felony', input: 'bool', ops: BOOL_OPS },
    { key: 'hasTicket', label: es ? '¿Tiene multas/tickets?' : 'Has tickets', input: 'bool', ops: BOOL_OPS },
    // ─ Referral ───────────────────────────────────────────────────────
    { key: 'referredByFirstName', label: es ? 'Referido por (nombre)' : 'Referred by (first name)', input: 'text', ops: TEXT_OPS },
    { key: 'referredByLastName',  label: es ? 'Referido por (apellido)' : 'Referred by (last name)', input: 'text', ops: TEXT_OPS },
    { key: 'referredByPhone',     label: es ? 'Referido por (teléfono)' : 'Referred by (phone)', input: 'text', ops: TEXT_OPS },
    // ─ Spouse (Phase 2B) ───────────────────────────────────────────────
    { key: 'spouseFirstName', label: es ? 'Cónyuge — nombre' : 'Spouse — first name', input: 'text', ops: TEXT_OPS },
    { key: 'spouseLastName',  label: es ? 'Cónyuge — apellido' : 'Spouse — last name', input: 'text', ops: TEXT_OPS },
    { key: 'spousePhone',     label: es ? 'Cónyuge — teléfono' : 'Spouse — phone', input: 'text', ops: TEXT_OPS },
    { key: 'spouseEmail',     label: es ? 'Cónyuge — email' : 'Spouse — email', input: 'text', ops: TEXT_OPS },
    { key: 'spouseEmployer',  label: es ? 'Cónyuge — empleador' : 'Spouse — employer', input: 'text', ops: TEXT_OPS },
    { key: 'spouseAnnualIncome', label: es ? 'Cónyuge — ingresos anuales' : 'Spouse — annual income', input: 'text', ops: TEXT_OPS },
    { key: 'spouseImmigrationStatus', label: es ? 'Cónyuge — estatus migratorio' : 'Spouse — immigration status', input: 'enumLabeled',
      options: IMMIGRATION_STATUS_OPTIONS(), labels: IMMIGRATION_LABELS(es),
      ops: ['is_any_of', 'is_none_of', 'eq', 'neq', 'is_empty', 'is_not_empty'] },
    { key: 'spouseImmigrationDocExpiration',
      label: es ? 'Cónyuge — vencimiento del documento' : 'Spouse — document expiration',
      input: 'date', ops: ['before', 'between', 'after', 'eq', 'is_empty', 'is_not_empty'] },
    { key: 'spouseAge',        label: es ? 'Cónyuge — edad' : 'Spouse — age', input: 'number', ops: NUM_OPS },
    { key: 'spouseDateOfBirth', label: es ? 'Cónyuge — fecha de nacimiento' : 'Spouse — date of birth', input: 'date', ops: DATE_OPS },
    // ─ Dependents (Phase 2B) ───────────────────────────────────────────
    // Filters on a derived integer column the backend recomputes on every
    // save, so this is a normal numeric comparison — no JSON SQL.
    { key: 'dependentsCount', label: es ? 'Dependientes (cantidad)' : 'Dependents (count)', input: 'number', ops: NUM_OPS },
    // ESTATUS MIGRATORIO DE LOS DEPENDIENTES (Lissette 2026-09-01: "en la
    // lista no sale poder escoger nada de los dependientes"). La COLUMNA
    // existía desde siempre; el FILTRO no, así que el dato se podía enseñar
    // pero no buscar — que en un reporte es casi lo mismo que no tenerlo.
    //
    // Sin 'eq': significa "tiene AL MENOS UN dependiente con ese estatus", y
    // un "es igual a" ahí se leería como "todos lo tienen", que es otra cosa.
    { key: 'dependentsImmigration', label: es ? 'Dependientes — estatus migratorio' : 'Dependents — immigration status', input: 'enumLabeled',
      options: IMMIGRATION_STATUS_OPTIONS(), labels: IMMIGRATION_LABELS(es),
      ops: ['is_any_of', 'is_none_of', 'is_empty', 'is_not_empty'] },
    // ─ Dates ──────────────────────────────────────────────────────────
    { key: 'age',         label: es ? 'Edad' : 'Age', input: 'number', ops: NUM_OPS },
    { key: 'dateOfBirth', label: es ? 'Fecha de nacimiento' : 'Date of birth', input: 'date', ops: DATE_OPS },
    { key: 'createdAt',   label: es ? 'Fecha de creación' : 'Created at', input: 'date', ops: DATE_OPS },
    // ─ Policy relation ────────────────────────────────────────────────
    // "Has any policy?" — the clear way to find clients WITH or WITHOUT a
    // policy (Sí/No toggle). Use this for "clientes sin póliza".
    { key: 'policy.any', label: es ? 'Tiene póliza' : 'Has any policy', input: 'bool', ops: BOOL_OPS },
    { key: 'policy.type', label: es ? 'Tipo de póliza' : 'Policy type', input: 'policyType',
      ops: ['eq', 'neq', 'is_any_of', 'is_none_of', 'is_empty', 'is_not_empty'] },
    { key: 'policy.carrier', label: es ? 'Aseguradora' : 'Carrier', input: 'text', ops: TEXT_OPS },
    { key: 'policy.planName', label: es ? 'Nombre del plan' : 'Plan name', input: 'text', ops: TEXT_OPS },
    { key: 'policy.policyNumber', label: es ? 'Número de póliza' : 'Policy number', input: 'text', ops: TEXT_OPS },
    { key: 'policy.leadType', label: es ? 'Origen de la póliza' : 'Policy lead source', input: 'text', ops: TEXT_OPS },
    { key: 'policy.processedBy', label: es ? 'Procesada por' : 'Processed by', input: 'text', ops: TEXT_OPS },
    { key: 'policy.premium', label: es ? 'Prima ($)' : 'Premium ($)', input: 'number', ops: NUM_OPS },
    { key: 'policy.status', label: es ? 'Estado de póliza' : 'Policy status', input: 'enumLabeled',
      // 'is_any_of' PRIMERO: es el que pidio Marilyn (2026-09-09) y el que
      // quiere casi todo reporte de bajas — "cancelada o vencida".
      options: POLICY_STATUS_OPTIONS, labels: POLICY_STATUS_LABELS(es),
      ops: ['is_any_of', 'is_none_of', 'eq', 'neq'] },
    { key: 'policy.expirationDate', label: es ? 'Vencimiento de póliza' : 'Policy expiration', input: 'date', ops: DATE_OPS },
    // Marilyn Sequera, 2026-08-21: llevaba el control de cancelados por meses y
    // no tenía por dónde tomar la fecha de la baja. "Vencimiento de póliza" es
    // otra cosa — una póliza puede cancelarse en julio y vencer en diciembre.
    { key: 'policy.cancellationDate', label: es ? 'Fecha de cancelación' : 'Cancellation date', input: 'date', ops: DATE_OPS },
    { key: 'policy.cancelReason', label: es ? 'Motivo de cancelación' : 'Cancellation reason', input: 'text', ops: DETAIL_OPS },
    // Las de PÓLIZA son otras que las del cliente, a propósito: una etiqueta de
    // póliza se va con la póliza. Hasta hoy no se podían reportar de ninguna
    // forma. "Está vacío" aquí significa "tiene alguna póliza SIN etiquetar",
    // que es lo que se quiere saber para ir a etiquetarla.
    { key: 'policy.tags', label: es ? 'Etiquetas de póliza' : 'Policy tags', input: 'enumLabeled',
      options: etiquetas.poliza || [],
      ops: ['is_any_of', 'is_none_of', 'eq', 'neq', 'is_empty', 'is_not_empty'] },
    { key: 'policy.memberId', label: es ? 'Member ID (póliza)' : 'Member ID (policy)', input: 'text', ops: TEXT_OPS },
    { key: 'policy.applicationNumber', label: es ? 'Número de aplicación (póliza)' : 'Application # (policy)', input: 'text', ops: DETAIL_OPS },
    // Campos que viven dentro de type_details (Lissette 2026-08-10: "usamos
    // muchos reportes"). Mismos operadores de texto que el # de aplicación —
    // ver POLICY_DETAIL_FILTERS en customReportFilter.ts. Si se añade uno aquí
    // hay que añadirlo allí también, o el backend lo rechaza como campo
    // desconocido.
    { key: 'policy.enrollmentPeriod', label: es ? 'Período de inscripción OE/SEP (póliza)' : 'Enrollment period OE/SEP (policy)', input: 'text', ops: DETAIL_OPS },
    { key: 'policy.generalAgent', label: es ? 'Agente general MGA (póliza)' : 'General agent MGA (policy)', input: 'text', ops: DETAIL_OPS },
    { key: 'policy.analystName', label: es ? 'Analista (póliza)' : 'Analyst (policy)', input: 'text', ops: DETAIL_OPS },
    { key: 'policy.medicareNumber', label: es ? 'Número de Medicare MBI (póliza)' : 'Medicare number MBI (policy)', input: 'text', ops: DETAIL_OPS },
    { key: 'policy.companyId', label: es ? 'ID de la compañía (póliza)' : 'Company ID (policy)', input: 'text', ops: DETAIL_OPS },
    { key: 'policy.planYear', label: es ? 'Plan del año (póliza)' : 'Plan year (policy)', input: 'text', ops: DETAIL_OPS },
    { key: 'policy.riskClass', label: es ? 'Clase de riesgo (póliza)' : 'Risk class (policy)', input: 'text', ops: DETAIL_OPS },
    { key: 'policy.effectiveDate', label: es ? 'Fecha efectiva (por año)' : 'Effective date (by year)', input: 'date', ops: DATE_OPS },
    { key: 'policy.paidThisMonth', label: es ? 'Pago (este mes)' : 'Payment (this month)', input: 'enumLabeled',
      options: ['paid', 'not_paid', 'grace'],
      labels: es ? { paid: 'Pagó', not_paid: 'No pagó', grace: 'Gracia' } : { paid: 'Paid', not_paid: 'Not paid', grace: 'Grace' },
      ops: ['eq', 'neq', 'is_any_of', 'is_none_of'] },
  ];
}

function opLabel(op, lang, field) {
  const es = lang === 'es';
  // Boolean-style fields (yes/no toggles) read more naturally as "is / is not".
  if (field && field.input === 'bool') {
    if (op === 'eq') return es ? 'es' : 'is';
    if (op === 'neq') return es ? 'no es' : 'is not';
  }
  return {
    eq:           es ? 'es igual a' : 'equals',
    neq:          es ? 'es distinto de' : 'not equals',
    gt:           es ? 'mayor que' : 'greater than',
    gte:          es ? 'mayor o igual que' : 'greater or equal',
    lt:           es ? 'menor que' : 'less than',
    lte:          es ? 'menor o igual que' : 'less or equal',
    contains:     es ? 'contiene' : 'contains',
    starts_with:  es ? 'empieza con' : 'starts with',
    ends_with:    es ? 'termina con' : 'ends with',
    is_empty:     es ? 'está vacío' : 'is empty',
    is_not_empty: es ? 'no está vacío' : 'is not empty',
    before:       es ? 'antes de' : 'before',
    after:        es ? 'después de' : 'after',
    between:      es ? 'entre' : 'between',
    // "es cualquiera de" y no "está en": describe lo que hace el filtro con
    // palabras de agente, no de base de datos.
    is_any_of:    es ? 'es cualquiera de' : 'is any of',
    is_none_of:   es ? 'no es ninguno de' : 'is none of',
  }[op] || op;
}

// EL TIPO DE POLIZA SE MANDA TAL CUAL ESTA EN EL CATALOGO ('aca', 'suppl'), NO
// TRADUCIDO AL ENUM DE LA BASE.
//
// Wendy Cercio, 2026-09-10: el reporte filtrado por SALUD le traia tambien los
// Suplementarios. Aqui habia una copia propia del mapa al enum, y SEIS ramos
// caian en el mismo valor `HEALTH` — Salud, Part D, Suplementario, Memberships,
// Hospitalizacion y Short Term. Eso rompia las dos mitades a la vez:
//
//   • el filtro devolvia las seis familias, dijeras la que dijeras;
//   • y el propio desplegable no se podia usar, porque cuatro opciones
//     compartian `value`: elegias "Suplementario" y el select volvia a pintar
//     "Salud", que es la primera opcion con ese valor. Se veia como si no
//     hubiera hecho caso.
//
// Ahora se manda el ramo exacto y el servidor lo resuelve contra el enum MAS el
// sello de `type_details.frontType` (`deskte-api/src/utils/tiposDePoliza.ts`).
// Los reportes ya GUARDADOS llevan dentro el enum viejo: el servidor lo sigue
// aceptando con su significado de entonces —la familia entera— y el desplegable
// lo ensena como tal en vez de quedarse en blanco (`opcionesDeTipo`).

/** Lo que ofrece el desplegable de tipo de poliza: los ramos del catalogo, mas
 *  —si lo que hay guardado es un enum de los de antes— una opcion de FAMILIA
 *  para que un reporte viejo se vea como lo que filtra.
 *
 *  `elegidos` son los valores que la condicion tiene puestos ahora mismo. Sin
 *  esto, abrir un reporte guardado en agosto ensenaria el tipo vacio mientras
 *  el reporte sigue filtrando por la familia: la pantalla diria una cosa y el
 *  resultado otra. */
function opcionesDeTipo(elegidos, lang) {
  const es = lang === 'es';
  const delCatalogo = POLICY_TYPES.map((pt) => ({
    v: pt.id,
    label: `${pt.icon} ${es ? (pt.labelEs || pt.label) : pt.label}`,
  }));
  const heredados = (Array.isArray(elegidos) ? elegidos : [elegidos])
    .map((v) => String(v ?? ''))
    .filter((v) => v && esValorDeEnum(v))
    .filter((v, i, a) => a.indexOf(v) === i)
    .map((v) => {
      const pt = POLICY_TYPES.find((x) => x.id === ENUM_A_TIPO[v]);
      const nombre = pt ? (es ? (pt.labelEs || pt.label) : pt.label) : v;
      return { v, label: es ? `${nombre} y sus ramos (guardado antes)` : `${nombre} and its sub-types (saved earlier)` };
    });
  return [...heredados, ...delCatalogo];
}

function emptyCondition() {
  return { type: 'condition', field: 'city', op: 'eq', value: '' };
}

function emptyGroup(operator = 'OR') {
  return { type: 'group', operator, children: [emptyCondition()] };
}

// Default empty filter tree — one AND group with one condition.
function emptyTree() {
  return { operator: 'AND', children: [emptyCondition()] };
}

// Accept reports saved in BOTH the MVP shape `{conditions: [...]}` and
// the Phase 2A shape `{operator, children}`. Normalize to the new shape
// for the in-memory builder so the recursive renderer only handles one
// representation.
function normalizeFilters(filters) {
  if (!filters || typeof filters !== 'object') return emptyTree();
  if (filters.operator && Array.isArray(filters.children)) {
    // Already in Phase 2A shape — defensively tag each node so the
    // builder discriminator (`type === 'group'`) always works.
    return tagNodes(filters);
  }
  if (Array.isArray(filters.conditions) && filters.conditions.length) {
    return {
      operator: 'AND',
      children: filters.conditions.map((c) => ({ type: 'condition', ...c })),
    };
  }
  return emptyTree();
}

// Walk the tree and ensure every node has a `type` discriminator.
function tagNodes(node) {
  if (node && typeof node === 'object' && Array.isArray(node.children)) {
    return {
      ...node,
      children: node.children.map((c) =>
        c && c.type === 'group'
          ? tagNodes(c)
          : { type: 'condition', ...c },
      ),
    };
  }
  return node;
}

// ─ Available result columns ───────────────────────────────────────────
// Lists the columns the user can choose to show in the result table.
// Keys map to fields on the rows the backend returns for /reports/custom/:id/run
// (which mirrors CLIENT_LIST_SELECT in deskte-api/reports.routes.ts).
//
// Names are stable strings — they get persisted in CustomReport.columns,
// so renaming a key requires a data migration.
// Grouped so the (now 35+) options stay scannable. Each group renders
// under a small heading; the saved value is still a flat list of keys.
// Every key here must have a matching renderer in getResultColumns()
// (ReportsClientsView.jsx) or the column is silently dropped at render.
function makeColumnCatalog(lang) {
  const es = lang === 'es';
  return [
    {
      group: es ? 'Cliente' : 'Client',
      cols: [
        { key: 'name',          label: es ? 'Cliente' : 'Client', required: true },
        { key: 'firstName',     label: es ? 'Nombre' : 'First name' },
        { key: 'lastName',      label: es ? 'Apellido' : 'Last name' },
        { key: 'status',        label: 'Status' },
        { key: 'source',        label: es ? 'Origen' : 'Source' },
        { key: 'tags',          label: es ? 'Etiquetas del cliente' : 'Client tags' },
        { key: 'policyTags',    label: es ? 'Etiquetas de póliza' : 'Policy tags' },
        { key: 'assignedAgent', label: es ? 'Agente' : 'Agent' },
        // *"y también poder agregar ver columna de handles"* (2026-09-15). La
        // rellena el SERVIDOR con el nombre ya resuelto, para que la tabla y el
        // Excel digan lo mismo.
        { key: 'attendedBy',    label: es ? 'Atiende (handle)' : 'Handled by' },
        { key: 'language',      label: es ? 'Idioma' : 'Language' },
      ],
    },
    {
      group: es ? 'Contacto' : 'Contact',
      cols: [
        { key: 'email',          label: 'Email' },
        { key: 'phone',          label: es ? 'Teléfono' : 'Phone' },
        { key: 'phoneSecondary', label: es ? 'Teléfono 2' : 'Phone 2' },
        { key: 'cityState',      label: es ? 'Ciudad / Estado' : 'City / State' },
        { key: 'address',        label: es ? 'Dirección' : 'Address' },
        { key: 'city',           label: es ? 'Ciudad' : 'City' },
        { key: 'state',          label: es ? 'Estado' : 'State' },
        { key: 'county',         label: es ? 'Condado' : 'County' },
        { key: 'zipCode',        label: es ? 'Código postal' : 'ZIP code' },
      ],
    },
    {
      group: es ? 'Edad y fechas' : 'Age & dates',
      cols: [
        { key: 'age',         label: es ? 'Edad' : 'Age' },
        { key: 'dateOfBirth', label: es ? 'Fecha de nacimiento' : 'Date of birth' },
        { key: 'createdAt',   label: es ? 'Creado' : 'Created' },
      ],
    },
    {
      group: es ? 'Hogar y familia' : 'Household & family',
      advanced: true,
      cols: [
        { key: 'maritalStatus',     label: es ? 'Estado civil' : 'Marital status' },
        { key: 'immigrationStatus', label: es ? 'Estatus migratorio' : 'Immigration status' },
        { key: 'smoker',            label: es ? '¿Fuma?' : 'Smoker?' },
        { key: 'employer',          label: es ? 'Empleador' : 'Employer' },
        { key: 'annualIncome',      label: es ? 'Ingresos anuales' : 'Annual income' },
        { key: 'householdSize',     label: es ? 'Tamaño del hogar' : 'Household size' },
        { key: 'insuredMembers',    label: es ? 'Miembros asegurados' : 'Insured members' },
        { key: 'dependentsCount',   label: es ? 'Dependientes' : 'Dependents' },
        { key: 'dependentsImmigration', label: es ? 'Migratorio (dependientes)' : 'Dependents immigration' },
        // La fecha, para poder LEERLA en el Excel además de filtrar por ella:
        // un reporte de "a quién se le vence" sin la fecha al lado obliga a
        // abrir ficha por ficha para saber a quién llamar primero.
        { key: 'immigrationDocExpiration', label: es ? 'Vence el documento' : 'Document expires' },
        { key: 'spouseImmigrationDocExpiration', label: es ? 'Vence el documento (cónyuge)' : 'Document expires (spouse)' },
      ],
    },
    {
      // ESTE GRUPO FALTABA, Y ES POR LO QUE LA QUEJA VOLVIO.
      //
      // Las doce columnas separadas estaban escritas en el renderizador desde
      // el 2026-09-01, pero NO aqui — que es donde se eligen. Existian y no
      // habia forma de pedirlas: la unica opcion de dependientes que ofrecia la
      // pantalla era la columna junta de arriba (Lissette 2026-09-02: "sigo
      // viendo los dependientes y sus estatus en una misma columna").
      //
      // Va JUSTO DEBAJO de la columna junta a proposito: es donde se mira
      // cuando lo que se quiere es separarla.
      group: es ? 'Dependientes, uno por columna' : 'Dependents, one per column',
      advanced: true,
      cols: COLUMNAS_DE_DEPENDIENTES.flatMap(({ i, claveNombre, claveEstatus }) => [
        { key: claveNombre,  label: etiquetaDeDependiente(i, es) },
        { key: claveEstatus, label: etiquetaDeEstatusDeDependiente(i, es) },
      ]),
    },
    {
      group: es ? 'Referido' : 'Referral',
      advanced: true,
      cols: [
        { key: 'referredBy',      label: es ? 'Referido por' : 'Referred by' },
        { key: 'referredByPhone', label: es ? 'Tel. del referidor' : 'Referrer phone' },
      ],
    },
    {
      group: es ? 'Cónyuge' : 'Spouse',
      advanced: true,
      cols: [
        { key: 'spouseName',              label: es ? 'Cónyuge' : 'Spouse' },
        { key: 'spouseEmployer',          label: es ? 'Empleador (cónyuge)' : 'Spouse employer' },
        { key: 'spouseAnnualIncome',      label: es ? 'Ingresos (cónyuge)' : 'Spouse income' },
        { key: 'spouseImmigrationStatus', label: es ? 'Migratorio (cónyuge)' : 'Spouse immigration' },
      ],
    },
    {
      group: es ? 'Póliza' : 'Policy',
      cols: [
        { key: 'policyStatus',     label: es ? 'Estado de póliza' : 'Policy status' },
        { key: 'policyType',       label: es ? 'Tipo de póliza' : 'Policy type' },
        { key: 'policyCarrier',    label: es ? 'Aseguradora' : 'Carrier' },
        { key: 'policyPlanName',   label: es ? 'Nombre del plan' : 'Plan name' },
        { key: 'policyNumber',     label: es ? 'Número de póliza' : 'Policy number' },
        { key: 'policyPremium',    label: es ? 'Prima' : 'Premium' },
        { key: 'policyMemberId',   label: 'Member ID' },
        { key: 'policyApplicationNumber', label: es ? 'Número de aplicación' : 'Application number' },
        { key: 'policyEffective',  label: es ? 'Fecha efectiva' : 'Effective date' },
        { key: 'policyPaidStatus', label: es ? 'Pago (este mes)' : 'Payment (this month)' },
        { key: 'policyExpiration', label: es ? 'Vencimiento de póliza' : 'Policy expires' },
        { key: 'policyCancellationDate', label: es ? 'Fecha de cancelación' : 'Cancellation date' },
        { key: 'policyCancelReason', label: es ? 'Motivo de cancelación' : 'Cancellation reason' },
      ],
    },
  ];
}
// Default columns shown when a report has none saved (legacy reports or
// a brand-new builder with no checkbox interactions yet).
export const DEFAULT_RESULT_COLUMNS = ['name', 'email', 'phone', 'cityState', 'status', 'age', 'createdAt'];

// Count the conditions inside a filter tree (recursive Phase 2A shape
// OR legacy MVP `{conditions:[]}`). Used for the "N condición(es)"
// label on saved report cards.
function countConditionsInFilter(filters) {
  if (!filters || typeof filters !== 'object') return 0;
  if (Array.isArray(filters.conditions)) return filters.conditions.length;
  let total = 0;
  const walk = (node) => {
    if (!node) return;
    if (node.type === 'group' || Array.isArray(node.children)) {
      (node.children || []).forEach(walk);
    } else {
      total += 1;
    }
  };
  walk(filters);
  return total;
}

// ─ Immutable tree helpers ─────────────────────────────────────────────
// `path` is an array of indices that points into nested `children`.
// For example, given:
//   tree = { children: [ A, { children: [B, C] } ] }
// the path [1, 0] resolves to node B.

function updateAtPath(tree, path, updater) {
  if (path.length === 0) return updater(tree);
  const [head, ...rest] = path;
  return {
    ...tree,
    children: tree.children.map((c, i) => (i === head ? updateAtPath(c, rest, updater) : c)),
  };
}

function removeAtPath(tree, path) {
  if (path.length === 0) return tree; // can't remove the root
  const parentPath = path.slice(0, -1);
  const removeIdx = path[path.length - 1];
  return updateAtPath(tree, parentPath, (parent) => ({
    ...parent,
    children: parent.children.filter((_, i) => i !== removeIdx),
  }));
}

function appendChildAtPath(tree, path, child) {
  return updateAtPath(tree, path, (node) => ({
    ...node,
    children: [...node.children, child],
  }));
}

// Builder modal — used for both "Nuevo reporte" and "Editar reporte".
function ReportBuilder({ initial, onClose, onSaved, lang, currentUserId = null }) {
  const es = lang === 'es';
  // Alfabetico, con Nombre y Apellido clavados arriba (Lissette 2026-08-28).
  // El catalogo se declara en el orden en que se fue programando; para BUSCAR
  // un campo entre 62 eso no sirve, porque no hay ningun sitio donde "deberia"
  // estar. La regla vive en utils.js para poder probarla sin React.
  // LOS CATÁLOGOS DE ETIQUETAS DE LA AGENCIA, para poder MARCARLAS en vez de
  // escribirlas. Se piden aquí y no por props porque esta ventana es la única
  // que los usa, y arrastrarlos por dos capas para un solo desplegable es peor.
  // Si falla, el campo cae solo a escribir la etiqueta a mano (ver makeFields).
  const [etiquetas, setEtiquetas] = useState({ cliente: [], poliza: [] });
  useEffect(() => {
    let vivo = true;
    getAgencySettings()
      .then((cfg) => {
        if (!vivo) return;
        const lista = (v) => (Array.isArray(v) ? v.map((x) => String(x ?? '').trim()).filter(Boolean) : []);
        setEtiquetas({ cliente: lista(cfg?.tagLibrary), poliza: lista(cfg?.policyTagLibrary) });
      })
      .catch(() => { /* sin catálogo, la etiqueta se escribe a mano */ });
    return () => { vivo = false; };
  }, []);
  // QUIÉN ATIENDE HOY en esta agencia. Se pide al servidor y no se arma con la
  // lista del equipo: la mitad de los "handles" de una cartera importada son
  // nombres de personas que todavía no tienen asiento en Deskte, y una lista
  // hecha solo de asientos dejaría fuera justo a esos — el filtro saldría vacío
  // en la agencia que acaba de importar, que es la que lo pidió.
  const [atiende, setAtiende] = useState(null);
  useEffect(() => {
    let vivo = true;
    getClientFilterOptions()
      .then((r) => { if (vivo) setAtiende(r?.atiende || { asientos: [], nombres: [] }); })
      .catch(() => { /* sin lista, el resto del constructor sigue sirviendo */ });
    return () => { vivo = false; };
  }, []);
  const fields = useMemo(() => ordenarCamposDeReporte(makeFields(lang, etiquetas, atiende)), [lang, etiquetas, atiende]);
  const columnCatalog = useMemo(() => makeColumnCatalog(lang), [lang]);
  const [name, setName] = useState(initial?.name || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [tree, setTree] = useState(() => normalizeFilters(initial?.filters));
  const [columns, setColumns] = useState(() =>
    Array.isArray(initial?.columns) && initial.columns.length
      ? initial.columns
      : DEFAULT_RESULT_COLUMNS,
  );
  const [sharedWithAgency, setSharedWithAgency] = useState(!!initial?.sharedWithAgency);
  // Quien puede EDITARLO ademas de mi (Maria Alejandra Yepez, 2026-09-05).
  // Compartir y dejar editar son dos cosas distintas: se comparte con TODO el
  // equipo, y se deja editar a las personas que se marquen aqui.
  const [editorIds, setEditorIds] = useState(
    Array.isArray(initial?.editorIds) ? initial.editorIds : [],
  );
  const [equipo, setEquipo] = useState([]);
  // Solo hace falta cuando se comparte: sin compartir, la lista no significa
  // nada porque nadie mas ve el reporte.
  useEffect(() => {
    if (!sharedWithAgency || equipo.length) return;
    let vivo = true;
    listUsers()
      .then((r) => { if (vivo) setEquipo((r?.users || []).filter((u) => u.id !== currentUserId)); })
      .catch(() => { /* sin lista, el resto del formulario sigue sirviendo */ });
    return () => { vivo = false; };
  }, [sharedWithAgency, equipo.length, currentUserId]);
  const alternarEditor = (id) => setEditorIds((a) => (
    a.includes(id) ? a.filter((x) => x !== id) : [...a, id]
  ));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  // The less-common column groups (Household / Referral / Spouse) hide
  // behind a "Show more" toggle so the picker isn't a wall of checkboxes.
  // Start expanded when the report being edited already uses one of them,
  // so a checked column is never hidden out of sight.
  const advancedKeys = useMemo(
    () => new Set(columnCatalog.filter((g) => g.advanced).flatMap((g) => g.cols.map((c) => c.key))),
    [columnCatalog],
  );
  const [showMore, setShowMore] = useState(() =>
    (Array.isArray(initial?.columns) ? initial.columns : []).some((k) => advancedKeys.has(k)),
  );

  const fieldFor = (key) => fields.find((f) => f.key === key) || fields[0];

  const toggleColumn = (key) => {
    setColumns((prev) => prev.includes(key)
      ? prev.filter((c) => c !== key)
      : [...prev, key]);
  };

  // One column group: a heading + a grid of checkboxes. Shared by the
  // always-visible groups and the ones behind the "Show more" toggle.
  const renderColGroup = (grp) => (
    <div key={grp.group}>
      <div style={{ fontSize: '.62rem', textTransform: 'uppercase', letterSpacing: '.06em', color: 'var(--c-text3,#45688a)', fontWeight: 700, marginBottom: '.35rem' }}>
        {grp.group}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(170px,1fr))', gap: '.35rem' }}>
        {grp.cols.map((col) => {
          const checked = columns.includes(col.key);
          return (
            <label key={col.key} style={{
              display: 'flex', alignItems: 'center', gap: '.4rem',
              fontSize: '.78rem', color: 'var(--c-text,#c8dff0)',
              cursor: col.required ? 'default' : 'pointer',
              opacity: col.required ? 0.7 : 1,
            }}>
              <input
                type="checkbox"
                checked={checked || col.required}
                disabled={col.required}
                onChange={() => !col.required && toggleColumn(col.key)}
                style={{ width: 14, height: 14 }}
              />
              {col.label}
            </label>
          );
        })}
      </div>
    </div>
  );

  // Mutations are routed through the path so a deeply-nested change
  // produces a new tree without touching siblings.
  const updateNode = (path, patch) => {
    setTree((prev) => updateAtPath(prev, path, (n) => ({ ...n, ...patch })));
  };
  const onFieldChange = (path, key) => {
    setTree((prev) => updateAtPath(prev, path, (n) => {
      const f = fieldFor(key);
      const op = f.ops.includes(n.op) ? n.op : f.ops[0];
      // Boolean fields need a concrete value so they don't silently default
      // to "No"; everything else starts blank for the user to fill in.
      const value = esOpDeLista(op) ? [] : (f.input === 'bool' ? 'true' : '');
      return { ...n, field: key, op, value };
    }));
  };
  const removeNode = (path) => {
    setTree((prev) => removeAtPath(prev, path));
  };
  const addCondition = (path) => {
    setTree((prev) => appendChildAtPath(prev, path, emptyCondition()));
  };
  const addGroup = (path) => {
    setTree((prev) => appendChildAtPath(prev, path, emptyGroup('OR')));
  };
  const toggleGroupOperator = (path) => {
    setTree((prev) => updateAtPath(prev, path, (n) => ({
      ...n,
      operator: n.operator === 'AND' ? 'OR' : 'AND',
    })));
  };

  const handleSave = async () => {
    setErr(null);
    if (!name.trim()) {
      setErr(es ? 'El reporte necesita un nombre.' : 'Report needs a name.');
      return;
    }
    // Force at least one column so the result table isn't blank.
    const finalColumns = columns.length > 0 ? columns : DEFAULT_RESULT_COLUMNS;
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim() || null,
        filters: tree,
        columns: finalColumns,
        sharedWithAgency,
        // Se manda siempre, tambien al dejar de compartir: asi la eleccion
        // queda hecha y vuelve tal cual si se comparte otra vez. El servidor
        // la limpia contra el equipo de verdad.
        editorIds,
      };
      const saved = initial?.id
        ? await updateCustomReport(initial.id, payload)
        : await createCustomReport(payload);
      onSaved(saved);
    } catch (e) {
      setErr(e.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 99999,
      background: 'rgba(0, 0, 0, 0.78)', backdropFilter: 'blur(6px)',
      display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
      padding: '4vh 1rem', overflowY: 'auto',
    }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: 'var(--c-surface,#0d1c2e)',
        border: '1px solid var(--c-border,#1a3550)',
        borderRadius: 14, width: '100%', maxWidth: 820,
        boxShadow: '0 32px 80px rgba(0,0,0,0.6)',
      }}>
        <div style={{
          padding: '1rem 1.25rem',
          borderBottom: '1px solid var(--c-border,#1a3550)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '1.05rem', color: 'var(--c-white,#eef6ff)' }}>
            {initial?.id ? (es ? 'Editar reporte' : 'Edit report') : (es ? 'Nuevo reporte' : 'New report')}
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-text2,#7aa3c0)', padding: 4 }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: '1.25rem' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '.85rem' }}>
            <div>
              <label style={{ fontSize: '.7rem', textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--c-text3,#45688a)', display: 'block', marginBottom: 4 }}>
                {es ? 'Nombre del reporte' : 'Report name'}
              </label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={es ? 'Ej. Clientes Florida con Auto activo' : 'e.g. Florida clients with active Auto'}
                className="dkt-inp"
                style={{ fontSize: '.92rem', padding: '.55rem .75rem', width: '100%' }}
                autoFocus
              />
            </div>
            <div>
              <label style={{ fontSize: '.7rem', textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--c-text3,#45688a)', display: 'block', marginBottom: 4 }}>
                {es ? 'Descripción (opcional)' : 'Description (optional)'}
              </label>
              <input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={es ? 'Para qué sirve este reporte' : 'What this report is for'}
                className="dkt-inp"
                style={{ fontSize: '.88rem', padding: '.55rem .75rem', width: '100%' }}
              />
            </div>

            <label style={{
              display: 'flex', alignItems: 'center', gap: '.5rem',
              background: 'rgba(0,212,255,.06)',
              border: '1px solid rgba(0,212,255,.2)',
              borderRadius: 8, padding: '.55rem .75rem',
              cursor: 'pointer', fontSize: '.85rem', color: 'var(--c-text,#c8dff0)',
            }}>
              <input
                type="checkbox"
                checked={sharedWithAgency}
                onChange={(e) => setSharedWithAgency(e.target.checked)}
                style={{ width: 16, height: 16, cursor: 'pointer' }}
              />
              <Users size={14} color="#00d4ff" />
              <span style={{ fontWeight: 600 }}>
                {es ? 'Compartir con mi equipo' : 'Share with my team'}
              </span>
            </label>

            {/* QUIEN PUEDE EDITARLO. Maria Alejandra Yepez, 2026-09-05: el
                equipo podia ver y ejecutar un reporte compartido, pero solo su
                autor podia tocarle los filtros. Aqui se elige a quien se le
                deja, y el resto sigue en solo lectura.

                Solo aparece si esta compartido: sin compartir, nadie mas ve el
                reporte y la lista no significaria nada. */}
            {sharedWithAgency && (
              <div style={{
                marginTop: '.5rem', padding: '.6rem .75rem', borderRadius: 8,
                background: 'var(--ci-bg-04)', border: '1px solid var(--c-border,#1a3550)',
              }}>
                <div style={{ fontSize: '.78rem', fontWeight: 700, color: 'var(--c-text,#c8dff0)', marginBottom: '.15rem' }}>
                  {es ? 'Todo el equipo puede verlo y ejecutarlo' : 'The whole team can view and run it'}
                </div>
                <div style={{ fontSize: '.74rem', color: 'var(--c-text3,#45688a)', marginBottom: '.5rem' }}>
                  {es
                    ? 'Marca a quién le dejas además CAMBIAR los filtros y las columnas. Los demás quedan en solo lectura.'
                    : 'Tick who may also CHANGE its filters and columns. Everyone else stays read-only.'}
                </div>
                {equipo.length === 0 ? (
                  <div style={{ fontSize: '.74rem', color: 'var(--c-text3,#45688a)', fontStyle: 'italic' }}>
                    {es ? 'No hay nadie más en tu equipo todavía.' : 'Nobody else on your team yet.'}
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.4rem' }}>
                    {equipo.map((u) => {
                      const puesto = editorIds.includes(u.id);
                      return (
                        <label
                          key={u.id}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: '.4rem',
                            padding: '.35rem .6rem', borderRadius: 7, cursor: 'pointer',
                            fontSize: '.78rem',
                            background: puesto ? 'rgba(0,212,255,.12)' : 'transparent',
                            border: `1px solid ${puesto ? 'rgba(0,212,255,.45)' : 'var(--c-border,#1a3550)'}`,
                            color: puesto ? 'var(--c-cyan,#00d4ff)' : 'var(--c-text2,#7aa3c0)',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={puesto}
                            onChange={() => alternarEditor(u.id)}
                            style={{ width: 14, height: 14, cursor: 'pointer' }}
                          />
                          {u.displayName || u.name || u.email}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            <div style={{ marginTop: '.4rem' }}>
              <div style={{ fontSize: '.7rem', textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--c-text3,#45688a)', marginBottom: '.5rem' }}>
                {es ? 'Filtros' : 'Filters'}
              </div>
              <GroupNode
                group={tree}
                path={[]}
                depth={0}
                fields={fields}
                fieldFor={fieldFor}
                lang={lang}
                onFieldChange={onFieldChange}
                onUpdateNode={updateNode}
                onRemove={removeNode}
                onAddCondition={addCondition}
                onAddGroup={addGroup}
                onToggleGroupOperator={toggleGroupOperator}
              />
            </div>

            <div style={{ marginTop: '.4rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '.5rem', gap: '.5rem' }}>
                <div style={{ fontSize: '.7rem', textTransform: 'uppercase', letterSpacing: '.05em', color: 'var(--c-text3,#45688a)' }}>
                  {es ? 'Columnas a mostrar' : 'Columns to show'}
                </div>
                <button
                  type="button"
                  onClick={() => setColumns(DEFAULT_RESULT_COLUMNS)}
                  style={{
                    background: 'transparent', border: 'none', cursor: 'pointer',
                    color: 'var(--c-cyan,#00d4ff)', fontSize: '.7rem', fontWeight: 600,
                    padding: 0,
                  }}
                >
                  {es ? 'Restablecer predeterminadas' : 'Reset to default'}
                </button>
              </div>
              <div style={{
                display: 'flex', flexDirection: 'column', gap: '.65rem',
                background: 'var(--c-surface2,#112538)',
                border: '1px solid var(--c-border,#1a3550)',
                borderRadius: 10, padding: '.65rem .8rem',
              }}>
                {columnCatalog.filter((g) => !g.advanced).map(renderColGroup)}
                {(() => {
                  const advGroups = columnCatalog.filter((g) => g.advanced);
                  if (!advGroups.length) return null;
                  const selCount = columns.filter((k) => advancedKeys.has(k)).length;
                  const names = advGroups.map((g) => g.group).join(' · ');
                  return (
                    <>
                      <button
                        type="button"
                        onClick={() => setShowMore((v) => !v)}
                        style={{
                          alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: '.3rem',
                          background: 'transparent', border: 'none', cursor: 'pointer',
                          color: 'var(--c-cyan,#00d4ff)', fontSize: '.74rem', fontWeight: 600,
                          padding: '.2rem 0',
                          borderTop: '1px solid var(--c-border,#1a3550)', width: '100%', justifyContent: 'flex-start', paddingTop: '.6rem',
                        }}
                      >
                        {showMore
                          ? (es ? '− Mostrar menos' : '− Show less')
                          : (es ? `+ Mostrar más campos (${names})` : `+ Show more fields (${names})`)}
                        {!showMore && selCount > 0 ? (es ? ` · ${selCount} activas` : ` · ${selCount} on`) : ''}
                      </button>
                      {showMore && advGroups.map(renderColGroup)}
                    </>
                  );
                })()}
              </div>
            </div>

            {err && (
              <div style={{ background: 'rgba(239,68,68,.08)', border: '1px solid rgba(239,68,68,.3)', borderRadius: 8, padding: '.6rem .85rem', color: '#fca5a5', fontSize: '.8rem' }}>
                {err}
              </div>
            )}
          </div>
        </div>

        <div style={{
          padding: '.85rem 1.25rem',
          borderTop: '1px solid var(--c-border,#1a3550)',
          display: 'flex', justifyContent: 'flex-end', gap: '.5rem',
        }}>
          <button onClick={onClose} style={{
            background: 'transparent', border: '1px solid var(--c-border,#1a3550)',
            color: 'var(--c-text2,#7aa3c0)', fontSize: '.82rem', fontWeight: 600,
            padding: '.5rem 1rem', borderRadius: 8, cursor: 'pointer',
          }}>
            {es ? 'Cancelar' : 'Cancel'}
          </button>
          <button onClick={handleSave} disabled={saving} style={{
            display: 'inline-flex', alignItems: 'center', gap: '.35rem',
            background: 'linear-gradient(135deg,#0099cc,#0055aa)', border: 'none',
            color: '#fff', fontSize: '.82rem', fontWeight: 700,
            padding: '.5rem 1rem', borderRadius: 8, cursor: saving ? 'wait' : 'pointer',
            opacity: saving ? 0.6 : 1,
          }}>
            <Save size={13} /> {saving ? (es ? 'Guardando…' : 'Saving…') : (es ? 'Guardar' : 'Save')}
          </button>
        </div>
      </div>
    </div>
  );
}

// Recursive renderer for a filter group. The root tree is also a group
// with `operator='AND'`, so the same component handles every level.
function GroupNode({
  group, path, depth, fields, fieldFor, lang,
  onFieldChange, onUpdateNode, onRemove, onAddCondition, onAddGroup, onToggleGroupOperator,
  // Cierto si CUALQUIER grupo por encima combina con "O" — basta uno, igual
  // que en la comprobación del servidor.
  dentroDeO = false,
}) {
  const es = lang === 'es';
  const isRoot = depth === 0;
  const oAqui = dentroDeO || group.operator === 'OR';
  const opLabelText = group.operator === 'AND'
    ? (es ? 'TODAS deben cumplirse (Y)' : 'ALL must match (AND)')
    : (es ? 'AL MENOS UNA debe cumplirse (O)' : 'ANY can match (OR)');
  const accent = group.operator === 'AND' ? '#00d4ff' : '#a855f7';

  return (
    <div style={{
      background: isRoot ? 'transparent' : 'rgba(168, 85, 247, .05)',
      border: isRoot ? '1px solid var(--c-border,#1a3550)' : `1px solid ${accent}40`,
      borderRadius: 10, padding: '.55rem .65rem',
      // Indent nested groups so the hierarchy is visible.
      marginLeft: isRoot ? 0 : 4,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: '.5rem', flexWrap: 'wrap', gap: '.4rem',
      }}>
        <button
          type="button"
          onClick={() => onToggleGroupOperator(path)}
          title={es ? 'Cambiar entre Y / O' : 'Toggle AND / OR'}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '.35rem',
            background: `${accent}1a`, border: `1px solid ${accent}55`,
            color: accent, fontSize: '.7rem', fontWeight: 700,
            padding: '.3rem .65rem', borderRadius: 6, cursor: 'pointer',
            textTransform: 'uppercase', letterSpacing: '.04em',
          }}
        >
          <FolderTree size={12} /> {opLabelText}
        </button>
        {!isRoot && (
          <button
            type="button"
            onClick={() => onRemove(path)}
            title={es ? 'Quitar grupo' : 'Remove group'}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: '#f87171', padding: 4,
            }}
          >
            <Trash2 size={13} />
          </button>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '.5rem' }}>
        {(group.children || []).map((child, idx) => {
          const childPath = [...path, idx];
          if (child.type === 'group') {
            return (
              <GroupNode
                key={idx}
                group={child}
                path={childPath}
                depth={depth + 1}
                fields={fields}
                fieldFor={fieldFor}
                lang={lang}
                onFieldChange={onFieldChange}
                onUpdateNode={onUpdateNode}
                onRemove={onRemove}
                onAddCondition={onAddCondition}
                onAddGroup={onAddGroup}
                onToggleGroupOperator={onToggleGroupOperator}
                dentroDeO={oAqui}
              />
            );
          }
          const f = fieldFor(child.field);
          return (
            <ConditionRow
              key={idx}
              condition={child}
              field={f}
              fields={fields}
              lang={lang}
              onFieldChange={(k) => onFieldChange(childPath, k)}
              // Al cambiar de operador el valor se limpia SIEMPRE, y con la
              // forma que toca: los de lista guardan un array. Dejar aquí el
              // '' de antes haría que "es cualquiera de" naciera sin lista y
              // no filtrara nada, en silencio.
              onOpChange={(op) => onUpdateNode(childPath, { op, value: esOpDeLista(op) ? [] : '' })}
              onValueChange={(value) => onUpdateNode(childPath, { value })}
              onRemove={() => onRemove(childPath)}
              canRemove={group.children.length > 1 || !isRoot}
              dentroDeO={oAqui}
            />
          );
        })}
      </div>

      <div style={{ display: 'flex', gap: '.4rem', marginTop: '.55rem' }}>
        <button
          type="button"
          onClick={() => onAddCondition(path)}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '.3rem',
            background: 'var(--c-surface2,#112538)',
            border: '1px dashed var(--c-border,#1a3550)',
            color: 'var(--c-text2,#7aa3c0)', fontSize: '.75rem', fontWeight: 600,
            padding: '.4rem .75rem', borderRadius: 8, cursor: 'pointer',
          }}
        >
          <Plus size={12} /> {es ? 'Condición' : 'Condition'}
        </button>
        {depth < 3 && (
          <button
            type="button"
            onClick={() => onAddGroup(path)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '.3rem',
              background: 'var(--c-surface2,#112538)',
              border: '1px dashed rgba(168,85,247,.4)',
              color: '#c4b5fd', fontSize: '.75rem', fontWeight: 600,
              padding: '.4rem .75rem', borderRadius: 8, cursor: 'pointer',
            }}
          >
            <Folder size={12} /> {es ? 'Grupo (Y/O)' : 'Group (AND/OR)'}
          </button>
        )}
      </div>
    </div>
  );
}

// One filter row inside the builder modal.
/**
 * CAMPOS QUE SOLO FUNCIONAN DENTRO DE UN GRUPO "Y".
 *
 * El vencimiento del documento migratorio vive dentro de un bloque CIFRADO, así
 * que la base de datos no lo puede comparar: el servidor trae candidatos y
 * afina después, ya descifrado. Dentro de un "Y" eso es inofensivo (se trae de
 * más y se recorta). Dentro de un "O" no: quitar una rama de un "O" cambia el
 * significado del grupo entero, y volver a aplicarla luego como si fuera un "Y"
 * da un tercer resultado que no es ninguno de los dos.
 *
 * El servidor lo RECHAZA al guardar (con su mensaje). Esto de aquí es para que
 * se sepa antes de llegar a ese punto, y no después de armar el reporte entero.
 * Ver CAMPOS_EN_MEMORIA en deskte-api/src/utils/customReportFilter.ts.
 */
const SOLO_CON_Y = new Set(['immigrationDocExpiration', 'spouseImmigrationDocExpiration']);

function ConditionRow({ condition, field, fields, lang, onFieldChange, onOpChange, onValueChange, onRemove, canRemove, dentroDeO }) {
  const needsValue = !['is_empty', 'is_not_empty'].includes(condition.op);
  // Este campo, en este sitio, no se va a poder guardar. Se dice AQUÍ y en
  // rojo: enterarse al pulsar "Guardar", con el reporte ya armado, es la peor
  // forma de descubrirlo.
  const noValeAqui = dentroDeO && SOLO_CON_Y.has(condition.field);
  return (
    <div style={{
      display: 'flex', gap: '.4rem', alignItems: 'center', flexWrap: 'wrap',
      background: 'var(--c-surface2,#112538)',
      border: noValeAqui ? '1px solid rgba(255,61,107,.55)' : '1px solid var(--c-border,#1a3550)',
      borderRadius: 10, padding: '.55rem .65rem',
    }}>
      <select value={condition.field} onChange={(e) => onFieldChange(e.target.value)} className="dkt-inp"
        style={{ fontSize: '.8rem', padding: '.35rem .55rem', width: 200, cursor: 'pointer' }}>
        {fields.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
      </select>
      <select value={condition.op} onChange={(e) => onOpChange(e.target.value)} className="dkt-inp"
        style={{ fontSize: '.8rem', padding: '.35rem .55rem', width: 160, cursor: 'pointer' }}>
        {field.ops.map((op) => <option key={op} value={op}>{opLabel(op, lang, field)}</option>)}
      </select>
      {needsValue && <ConditionValueInput field={field} op={condition.op} value={condition.value} onChange={onValueChange} lang={lang} />}
      <div style={{ flex: 1 }} />
      <button onClick={onRemove} disabled={!canRemove}
        title={lang === 'es' ? 'Quitar' : 'Remove'}
        style={{
          background: 'transparent', border: 'none', cursor: canRemove ? 'pointer' : 'not-allowed',
          color: canRemove ? '#f87171' : 'var(--c-text3,#45688a)', padding: 4,
          opacity: canRemove ? 1 : 0.4,
        }}>
        <Trash2 size={14} />
      </button>
      {noValeAqui && (
        <div style={{ flexBasis: '100%', fontSize: '.7rem', color: '#ff3d6b', lineHeight: 1.4 }}>
          {lang === 'es'
            ? 'Este campo solo funciona dentro de un grupo “Y” (TODAS deben cumplirse). Sácalo del grupo “O” o el reporte no se podrá guardar.'
            : 'This field only works inside an “AND” group (ALL must match). Move it out of the “OR” group or the report cannot be saved.'}
        </div>
      )}
    </div>
  );
}

/**
 * VARIOS VALORES ESCRITOS A MANO, para los campos que no tienen catálogo:
 * Estado, Ciudad, Condado, Aseguradora, Empleador, Origen…
 *
 * Marilyn Sequera, 2026-09-09: *"habilitar la opción de que se puedan
 * seleccionar varios estados 'es cualquiera de'"*. En «Estatus migratorio» eso
 * son casillas porque la lista está cerrada; un estado del país, una
 * aseguradora o una ciudad no salen de ninguna lista de Deskte — se escriben.
 *
 * Se añade con Enter, con coma o al salir del cuadro. Las TRES: un agente que
 * escribe "FL," espera que se haya guardado, y uno que escribe "FL" y hace
 * clic en «Ejecutar» también. Perder el último valor por no haber pulsado
 * Enter es exactamente el fallo silencioso que este reporte no puede permitirse.
 *
 * El tope de 60 es el mismo del servidor (MAX_LIST_VALUES): pasarse no da
 * error, se corta la condición entera.
 */
const MAX_VALORES = 60;
function ValoresEscritos({ valores, onChange, lang }) {
  const es = lang === 'es';
  const [texto, setTexto] = useState('');

  const agregar = (bruto) => {
    // Una pegada de "FL, GA, TX" entra como tres, no como una.
    const nuevos = String(bruto || '')
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean)
      // Sin repetir: dos veces el mismo valor no cambia el resultado y ocupa
      // sitio del tope.
      .filter((v) => !valores.some((x) => x.toLowerCase() === v.toLowerCase()));
    if (!nuevos.length) { setTexto(''); return; }
    onChange([...valores, ...nuevos].slice(0, MAX_VALORES));
    setTexto('');
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap',
        border: '1px solid var(--c-border,#1a3550)', borderRadius: 8,
        padding: '.2rem .3rem', minWidth: 200, background: 'var(--c-surface,#0d1b2a)',
      }}>
        {valores.map((v) => (
          <span key={v} style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)',
            borderRadius: 6, padding: '1px 4px 1px 7px', fontSize: '.74rem',
          }}>
            {v}
            <button type="button" title={es ? 'Quitar' : 'Remove'}
              onClick={() => onChange(valores.filter((x) => x !== v))}
              style={{ background: 'none', border: 'none', color: 'var(--c-text3,#45688a)', cursor: 'pointer', padding: '0 2px', fontSize: '.8rem', lineHeight: 1 }}>
              ×
            </button>
          </span>
        ))}
        <input
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); agregar(texto); }
            // Borrar hacia atrás con el cuadro vacío quita el último, como en
            // cualquier campo de destinatarios.
            else if (e.key === 'Backspace' && !texto && valores.length) onChange(valores.slice(0, -1));
          }}
          // Al salir se guarda lo que quedó escrito: ver la nota de arriba.
          onBlur={() => agregar(texto)}
          placeholder={valores.length >= MAX_VALORES
            ? (es ? 'tope alcanzado' : 'limit reached')
            : (es ? 'escribe y Enter…' : 'type and press Enter…')}
          disabled={valores.length >= MAX_VALORES}
          className="dkt-inp"
          style={{ fontSize: '.8rem', padding: '.25rem .4rem', width: 130, border: 'none', background: 'transparent' }} />
      </div>
      {/* Sin ningún valor la condición no filtra. Callarlo sería repetir el
          fallo que trajo todo esto: un reporte que no hace lo que parece. */}
      {valores.length === 0 && (
        <span style={{ fontSize: '.68rem', color: 'var(--c-amber,#ffb700)' }}>
          {es ? 'escribe al menos uno' : 'type at least one'}
        </span>
      )}
    </div>
  );
}

// Value input — type/widget switches based on the field + op.
function ConditionValueInput({ field, op, value, onChange, lang }) {
  // VARIOS VALORES → casillas. Se reutiliza el MISMO selector de Clientes,
  // Pólizas y Correos Masivos: dos copias se separan en cuanto alguien toque
  // una (E-138). No un `<select multiple>` del navegador, que obliga a
  // mantener Ctrl pulsado y pierde la selección anterior de un clic.
  if (esOpDeLista(op)) {
    const es = lang === 'es';
    const elegidas = Array.isArray(value) ? value : (value ? [value] : []);
    // El tipo de póliza no lleva `options` en el catálogo: su lista la arma
    // `opcionesDeTipo`, LA MISMA que usa su desplegable de un solo valor unas
    // pantallas más abajo. Escribirla a mano aquí es como se llegó al fallo de
    // Wendy: dos listas del mismo campo que se separan sin que nada avise.
    const tipos = field.input === 'policyType' ? opcionesDeTipo(elegidas, lang) : null;
    const delCatalogo = tipos ? tipos.map((o) => o.v) : (field.options || []);
    // LO QUE YA ESTÁ MARCADO ENTRA SIEMPRE EN LA LISTA, esté o no en el
    // catálogo de hoy. FiltroMultiple limpia solo lo que no aparece en sus
    // opciones —bien para la lista de clientes, donde el catálogo es lo que hay
    // a la vista—, pero aquí el catálogo llega del servidor un instante DESPUÉS
    // de abrir el reporte: sin esto, abrir un reporte guardado y volver a
    // guardarlo le borraba los valores marcados sin decir nada, y el reporte
    // pasaba a devolver la cartera entera.
    const opciones = [...delCatalogo, ...elegidas.filter((v) => !delCatalogo.includes(v))];
    const etiquetaDeTipo = (v) => (tipos.find((o) => o.v === v) || {}).label || v;
    const leerValor = (v) => field.labels?.[v] || (field.etiquetaDe ? field.etiquetaDe(v) : v);
    // CAMPO DE TEXTO LIBRE (Estado, Ciudad, Aseguradora, Empleador…): no hay
    // catálogo del que marcar, así que se escriben. Antes estos campos ni
    // ofrecían "es cualquiera de"; ofrecerlo con la lista de casillas vacía
    // habría sido un filtro que no se puede rellenar.
    if (!opciones.length) {
      return <ValoresEscritos valores={elegidas} onChange={onChange} lang={lang} />;
    }
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <FiltroMultiple
          opciones={opciones}
          etiquetaDe={field.input === 'policyType' ? etiquetaDeTipo : leerValor}
          seleccion={elegidas}
          onChange={onChange}
          icono="☑️"
          textoTodas={es ? '— elegir —' : '— pick —'}
          titulo={field.label}
          lang={lang} />
        {/* Sin nada marcado la condición no filtra. Callarlo sería repetir el
            fallo que trajo esto: un reporte que no hace lo que parece. */}
        {elegidas.length === 0 && (
          <span style={{ fontSize: '.68rem', color: 'var(--c-amber,#ffb700)' }}>
            {es ? 'marca al menos uno' : 'pick at least one'}
          </span>
        )}
      </div>
    );
  }
  // "between" → render two inputs.
  if (op === 'between') {
    const [a = '', b = ''] = Array.isArray(value) ? value : ['', ''];
    const t = field.input === 'date' ? 'date' : field.input === 'number' ? 'number' : 'text';
    return (
      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
        <input type={t} value={a} onChange={(e) => onChange([e.target.value, b])}
          className="dkt-inp" style={{ fontSize: '.8rem', padding: '.35rem .55rem', width: 130 }} />
        <span style={{ fontSize: '.7rem', color: 'var(--c-text3,#45688a)' }}>{lang === 'es' ? 'y' : 'and'}</span>
        <input type={t} value={b} onChange={(e) => onChange([a, e.target.value])}
          className="dkt-inp" style={{ fontSize: '.8rem', padding: '.35rem .55rem', width: 130 }} />
      </div>
    );
  }
  if (field.input === 'bool') {
    // Stored as the strings 'true' / 'false'; the backend coerces them.
    return (
      <select value={value === '' ? 'true' : String(value)} onChange={(e) => onChange(e.target.value)} className="dkt-inp"
        style={{ fontSize: '.8rem', padding: '.35rem .55rem', width: 120, cursor: 'pointer' }}>
        <option value="true">{lang === 'es' ? 'Sí' : 'Yes'}</option>
        <option value="false">{lang === 'es' ? 'No' : 'No'}</option>
      </select>
    );
  }
  if (field.input === 'enum') {
    return (
      <select value={value || ''} onChange={(e) => onChange(e.target.value)} className="dkt-inp"
        style={{ fontSize: '.8rem', padding: '.35rem .55rem', width: 180, cursor: 'pointer' }}>
        <option value="">{lang === 'es' ? '— elegir —' : '— pick —'}</option>
        {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    );
  }
  if (field.input === 'enumLabeled') {
    // Same as enum, but show a human label while keeping the raw value
    // as what gets stored / sent to the backend.
    //
    // El valor guardado entra en la lista aunque no esté en el catálogo de hoy,
    // por lo mismo que arriba: si no, un reporte guardado se abre con el campo
    // en blanco mientras el catálogo llega, y el desplegable dice una cosa
    // distinta de lo que el reporte filtra.
    const opciones = field.options.includes(value) || !value
      ? field.options
      : [...field.options, value];
    const leer = (o) => field.labels?.[o] || (field.etiquetaDe ? field.etiquetaDe(o) : o);
    return (
      <select value={value || ''} onChange={(e) => onChange(e.target.value)} className="dkt-inp"
        style={{ fontSize: '.8rem', padding: '.35rem .55rem', width: 200, cursor: 'pointer' }}>
        <option value="">{lang === 'es' ? '— elegir —' : '— pick —'}</option>
        {opciones.map((o) => <option key={o} value={o}>{leer(o)}</option>)}
      </select>
    );
  }
  if (field.input === 'policyType') {
    return (
      <select value={value || ''} onChange={(e) => onChange(e.target.value)} className="dkt-inp"
        style={{ fontSize: '.8rem', padding: '.35rem .55rem', width: 220, cursor: 'pointer' }}>
        <option value="">{lang === 'es' ? '— tipo —' : '— type —'}</option>
        {/* Cada opción lleva SU ramo, y por eso ninguna repite `value`: cuando
            cuatro lo compartían, elegir "Suplementario" repintaba "Salud". El
            servidor resuelve el ramo contra el enum más el sello de
            type_details.frontType, que es lo único que separa a los seis que
            se guardan como HEALTH. */}
        {opcionesDeTipo(value, lang).map((o) => (
          <option key={o.v} value={o.v}>{o.label}</option>
        ))}
      </select>
    );
  }
  if (field.input === 'date') {
    return (
      <DateField value={value || ''} onChange={(e) => onChange(e.target.value)}
        className="dkt-inp" style={{ fontSize: '.8rem', padding: '.35rem .55rem', width: 160 }} />
    );
  }
  if (field.input === 'number') {
    return (
      <input type="number" value={value ?? ''} onChange={(e) => onChange(e.target.value)}
        className="dkt-inp" style={{ fontSize: '.8rem', padding: '.35rem .55rem', width: 120 }} />
    );
  }
  return (
    <input type="text" value={value || ''} onChange={(e) => onChange(e.target.value)}
      className="dkt-inp" style={{ fontSize: '.8rem', padding: '.35rem .55rem', width: 200 }} />
  );
}

// Main tab: list of saved reports + builder modal + run callback.
export function CustomReportsTab({ lang = 'en', currentUser, onRun }) {
  const es = lang === 'es';
  const [reports, setReports] = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [building, setBuilding] = useState(null); // null | 'new' | {report}
  const [busy, setBusy] = useState(null);

  // Who can create / edit / delete reports. Agents and owners always can.
  // A seat (frontend role "assistant") can too, as long as their supervising
  // agent granted "reports.view" — the same permission that lets them reach
  // this screen. They can only manage their OWN reports (edit/delete stay
  // owner-gated below); shared reports from teammates remain read-only.
  const isSeat = currentUser?.role === 'assistant';
  const canManage = !currentUser
    || ['agent', 'agency_owner', 'agency_admin', 'solo_agent'].includes(currentUser.role)
    || (isSeat && hasSeatPermission(currentUser.seatPermissions, 'reports.view'));

  const load = async () => {
    setLoadErr(null);
    try {
      const list = await listCustomReports();
      setReports(list);
    } catch (e) {
      setLoadErr(e.message || String(e));
      setReports([]);
    }
  };

  useEffect(() => { load(); }, []);

  const handleRun = async (report) => {
    setBusy(report.id);
    try {
      const { rows, meta } = await runCustomReport(report.id);
      // Surface the saved column selection so the parent's result table
      // renders the right columns (and exports them). Falls back to the
      // default set when the saved report doesn't have a column list yet.
      const picked = Array.isArray(report.columns) && report.columns.length > 0;
      const columns = picked ? report.columns : DEFAULT_RESULT_COLUMNS;
      // `columnsPicked` says whether the agent ACTUALLY chose these columns or
      // whether they're just the fallback set. The result view uses it to decide
      // what an export downloads by default: a report built around a column
      // selection exports that selection (Marilyn 2026-08-24 — pedía columnas
      // concretas y el Excel bajaba todo), one without a selection keeps
      // downloading every column (Lissette 2026-07-15).
      onRun(report, rows, { ...meta, columns, columnsPicked: picked });
    } catch (e) {
      toast.error((es ? 'Error al ejecutar: ' : 'Error running: ') + e.message);
    } finally {
      setBusy(null);
    }
  };

  const handleDelete = async (report) => {
    if (!(await confirmDialog({ message: es ? `¿Eliminar el reporte "${report.name}"?` : `Delete the report "${report.name}"?`, danger: true }))) return;
    try {
      await deleteCustomReport(report.id);
      toast.success(es ? 'Reporte eliminado' : 'Report deleted');
      load();
    } catch (e) {
      toast.error((es ? 'Error al eliminar: ' : 'Error deleting: ') + e.message);
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div>
          <div style={{ fontSize: '.78rem', color: 'var(--c-text2,#7aa3c0)' }}>
            {es
              ? 'Reportes que vos construís combinando los filtros que quieras. Se guardan para reusar.'
              : 'Reports you build by combining whichever filters you want. Saved for reuse.'}
          </div>
        </div>
        {canManage && (
          <button onClick={() => setBuilding('new')} style={{
            display: 'inline-flex', alignItems: 'center', gap: '.35rem',
            background: 'linear-gradient(135deg,#0099cc,#0055aa)', border: 'none',
            color: '#fff', fontSize: '.82rem', fontWeight: 700,
            padding: '.5rem 1rem', borderRadius: 8, cursor: 'pointer',
            boxShadow: '0 4px 14px rgba(0,153,204,.25)',
          }}>
            <Plus size={14} /> {es ? 'Nuevo reporte' : 'New report'}
          </button>
        )}
      </div>

      {loadErr && (
        <div style={{ background: 'rgba(239,68,68,.08)', border: '1px solid rgba(239,68,68,.3)', borderRadius: 10, padding: '.75rem 1rem', color: '#fca5a5', fontSize: '.85rem', marginBottom: '1rem' }}>
          {loadErr}
        </div>
      )}

      {reports === null ? (
        <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--c-text3,#45688a)' }}>
          {es ? 'Cargando…' : 'Loading…'}
        </div>
      ) : reports.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: '3rem 1rem',
          background: 'var(--c-surface,#0d1c2e)',
          border: '1px dashed var(--c-border,#1a3550)',
          borderRadius: 12,
        }}>
          <Sparkles size={28} color="#45688a" style={{ marginBottom: '.5rem' }} />
          <div style={{ color: 'var(--c-text2,#7aa3c0)', fontSize: '.92rem', marginBottom: '.4rem', fontWeight: 600 }}>
            {es ? 'Todavía no tenés reportes guardados' : "You haven't saved any reports yet"}
          </div>
          <div style={{ color: 'var(--c-text3,#45688a)', fontSize: '.78rem' }}>
            {es ? 'Hacé clic en "Nuevo reporte" para armar el primero.' : 'Click "New report" to build your first one.'}
          </div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(300px,1fr))', gap: '.85rem' }}>
          {reports.map((r) => {
            // El servidor manda TRES cosas por fila, y no son la misma:
            //   isOwner  -> lo hice yo (decide la etiqueta de la tarjeta).
            //   canEdit  -> puedo tocarle filtros y columnas. Ademas del dueno,
            //               los editores de un compartido y el dueno de la
            //               agencia (Maria Alejandra Yepez, 2026-09-05).
            //   canShare -> puedo decidir quien mas edita, y borrarlo.
            // Antes el boton de Editar miraba `isOwner`, y por eso un reporte
            // compartido era de solo lectura para todo el equipo menos su autor.
            // La regla vive en el servidor (utils/permisoDeReportes.ts) y aqui
            // solo se obedece: repetirla seria dos verdades que se separan.
            const isOwner = r.isOwner !== false;
            const puedoEditar = r.canEdit !== undefined ? !!r.canEdit : isOwner;
            const puedoBorrar = r.canShare !== undefined ? !!r.canShare : isOwner;
            const showEdit = canManage && puedoEditar;
            const showDelete = canManage && puedoBorrar;
            // Count conditions across the recursive tree (Phase 2A) AND
            // the legacy flat shape, so cards stay informative for both.
            const conditionCount = countConditionsInFilter(r.filters);
            const ownerName = r.owner?.displayName || r.owner?.email || (es ? 'tu equipo' : 'your team');
            return (
            <div key={r.id} style={{
              background: 'var(--c-surface,#0d1c2e)',
              border: isOwner
                ? '1px solid var(--c-border,#1a3550)'
                : '1px solid rgba(0,212,255,.25)',
              borderRadius: 12, padding: '1rem',
              display: 'flex', flexDirection: 'column', gap: '.55rem',
            }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '.5rem' }}>
                <div style={{
                  fontFamily: 'var(--font-display)', fontWeight: 700,
                  fontSize: '.95rem', color: 'var(--c-white,#eef6ff)',
                  lineHeight: 1.25, flex: 1, overflowWrap: 'anywhere',
                }}>
                  {r.name}
                </div>
                {isOwner ? (
                  r.sharedWithAgency ? (
                    <span title={es ? 'Compartido con tu equipo' : 'Shared with your team'} style={{
                      fontSize: '.6rem', fontWeight: 700, letterSpacing: '.05em',
                      background: 'rgba(0, 212, 255, .15)',
                      color: '#00d4ff', padding: '.15rem .4rem', borderRadius: 4,
                      textTransform: 'uppercase', whiteSpace: 'nowrap',
                      display: 'inline-flex', alignItems: 'center', gap: 3,
                    }}>
                      <Users size={9} /> {es ? 'Equipo' : 'Team'}
                    </span>
                  ) : (
                    <span style={{
                      fontSize: '.6rem', fontWeight: 700, letterSpacing: '.05em',
                      background: 'rgba(168, 85, 247, .15)',
                      color: '#c4b5fd', padding: '.15rem .4rem', borderRadius: 4,
                      textTransform: 'uppercase', whiteSpace: 'nowrap',
                    }}>
                      {es ? 'Tuyo' : 'Yours'}
                    </span>
                  )
                ) : (
                  <span title={es ? `Compartido por ${ownerName}` : `Shared by ${ownerName}`} style={{
                    fontSize: '.6rem', fontWeight: 700, letterSpacing: '.05em',
                    background: 'rgba(0, 212, 255, .15)',
                    color: '#00d4ff', padding: '.15rem .4rem', borderRadius: 4,
                    textTransform: 'uppercase', whiteSpace: 'nowrap',
                    display: 'inline-flex', alignItems: 'center', gap: 3,
                  }}>
                    <Users size={9} /> {es ? 'Por' : 'By'} {ownerName.split(' ')[0]}
                  </span>
                )}
              </div>
              {/* A quien le dieron permiso tiene que VERLO. Sin este renglon el
                  unico indicio seria que aparece un boton de lapiz donde antes
                  no habia, y eso se descubre por casualidad. */}
              {!isOwner && puedoEditar && (
                <div style={{
                  fontSize: '.68rem', fontWeight: 600, color: '#34d399',
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                }}>
                  <Edit2 size={10} /> {es ? 'Puedes editarlo' : 'You can edit it'}
                </div>
              )}
              {r.description && (
                <div style={{ fontSize: '.76rem', color: 'var(--c-text2,#7aa3c0)', lineHeight: 1.45 }}>
                  {r.description}
                </div>
              )}
              <div style={{ fontSize: '.66rem', color: 'var(--c-text3,#45688a)', fontFamily: 'var(--font-mono)' }}>
                {conditionCount} {es ? 'condición(es)' : 'condition(s)'}
              </div>
              <div style={{ display: 'flex', gap: '.35rem', marginTop: '.25rem' }}>
                <button
                  onClick={() => handleRun(r)}
                  disabled={busy === r.id}
                  style={{
                    flex: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '.3rem',
                    background: 'linear-gradient(135deg,#0099cc,#0055aa)', border: 'none',
                    color: '#fff', fontSize: '.78rem', fontWeight: 700,
                    padding: '.42rem .65rem', borderRadius: 7, cursor: busy === r.id ? 'wait' : 'pointer',
                    opacity: busy === r.id ? 0.6 : 1,
                  }}>
                  <Play size={12} /> {busy === r.id ? (es ? 'Ejecutando…' : 'Running…') : (es ? 'Ejecutar' : 'Run')}
                </button>
                {showEdit && (
                    <button onClick={() => setBuilding({ report: r })}
                      title={es ? 'Editar' : 'Edit'}
                      style={{
                        background: 'var(--c-surface2,#112538)',
                        border: '1px solid var(--c-border,#1a3550)',
                        color: 'var(--c-text2,#7aa3c0)',
                        padding: '.42rem', borderRadius: 7, cursor: 'pointer',
                      }}>
                      <Edit2 size={12} />
                    </button>
                )}
                {showDelete && (
                    <button onClick={() => handleDelete(r)}
                      title={es ? 'Eliminar' : 'Delete'}
                      style={{
                        background: 'var(--c-surface2,#112538)',
                        border: '1px solid rgba(239,68,68,.25)',
                        color: '#f87171',
                        padding: '.42rem', borderRadius: 7, cursor: 'pointer',
                      }}>
                      <Trash2 size={12} />
                    </button>
                )}
              </div>
            </div>
          );})}
        </div>
      )}

      {building && (
        <ReportBuilder
          initial={building === 'new' ? null : building.report}
          lang={lang}
          currentUserId={currentUser?.id || null}
          onClose={() => setBuilding(null)}
          onSaved={(saved) => {
            setBuilding(null);
            toast.success(es ? 'Reporte guardado' : 'Report saved');
            load();
          }}
        />
      )}
    </div>
  );
}
